import React from "react";

type AngularHeaders = { [k: string]: string } | (() => { [k: string]: string });
type DownloadResponse = { data: ArrayBuffer; headers: AngularHeaders };

type Props = {
  request: () => Promise<DownloadResponse>;
  filename?: string;
  label?: string;
  icon?: string;
  className?: string;
  $rootScope?: any;
};

function parseDispositionFilename(cd: string | null): string | null {
  if (!cd) return null;
  const rfc = /filename\*=(?:UTF-8''|)([^;]+)/i.exec(cd);
  if (rfc?.[1]) {
    try {
      return decodeURIComponent(rfc[1].replace(/['"]/g, "").trim());
    } catch {
      return rfc[1].replace(/['"]/g, "").trim();
    }
  }
  const simple = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(cd);
  if (simple?.[1]) return simple[1].replace(/['"]/g, "").trim();
  return null;
}

function getHeader(headers: AngularHeaders, name: string): string | null {
  try {
    const h = typeof headers === "function" ? headers() : headers;
    const lower = name.toLowerCase();
    return (h[lower] ?? h[name] ?? null) as string | null;
  } catch {
    return null;
  }
}

export default function BfFileDownload({ request, filename, label = "Download", icon, className, $rootScope }: Props) {
  const [status, setStatus] = React.useState<string | null>(null);
  const busy = status === "Preparing…" || status === "Downloading…";

  const emit = (name: string, ...args: any[]) => {
    try {
      $rootScope?.$evalAsync?.(() => $rootScope.$emit(name, ...args));
    } catch {}
    try {
      window.dispatchEvent(new CustomEvent(name, { detail: args }));
    } catch {}
  };

  async function onClick() {
    if (busy) return;
    try {
      emit("download-init");
      setStatus("Preparing…");

      const res = await request();

      emit("download-start");
      setStatus("Downloading…");

      const cd = getHeader(res.headers, "content-disposition");
      const ct = getHeader(res.headers, "content-type") || "application/octet-stream";
      const suggested = parseDispositionFilename(cd) || filename || "download.bin";

      const blob = new Blob([res.data], { type: ct });
      emit("downloaded", blob, suggested);

      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = suggested.replace(/[/\\?%*:|"<>]/g, "_");
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);

      setStatus("Done");
      // Success: auto-clear after a moment
      setTimeout(() => setStatus(null), 1200);
    } catch (e: any) {
      console.error("[BfFileDownload] error", e);
      emit("download-failed", e?.message ?? String(e));
      // Error: keep the message until the user retries
      setStatus(`Failed: ${e?.message ?? String(e)}`);
    }
  }

  return (
    <div className={className || ""} style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
      {status && (
        <span style={{ fontSize: 12, color: status.startsWith("Failed") ? "#b91c1c" : "#606a76" }} aria-live="polite">
          {status}
        </span>
      )}
      <button
        type="button"
        onClick={onClick}
        disabled={busy}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: "8px 12px",
          borderRadius: 8,
          border: "1px solid #d1d5db",
          background: busy ? "#f3f4f6" : "#fff",
          color: "#111",
          cursor: busy ? "default" : "pointer",
        }}
        aria-busy={busy}
        aria-disabled={busy}
      >
        {icon && <i className={icon} aria-hidden="true" />}
        <span>{busy ? "Working…" : label}</span>
      </button>
    </div>
  );
}
